import Button from '@mui/material/Button';
import { Link, Tooltip } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useBoolean } from 'src/hooks/use-boolean';

import { fDateTime } from 'src/utils/format-time';
import { hasPermission } from 'src/utils/has-permissions';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';

import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { IPackagingReturnListItem } from 'src/types/packaging-return';

type Props = {
  row: IPackagingReturnListItem;
  onEditRow: VoidFunction;
  onDeleteRow: VoidFunction;
  // Backend restricts edit/delete to the most recently created return —
  // this row is only actionable when it's confirmed (by the list view) to
  // be that record.
  isEditable: boolean;
};

export default function PackagingReturnTableRow({
  row,
  onEditRow,
  onDeleteRow,
  isEditable,
}: Props) {
  const { _id, returnCode, receivedBy, returnDate } = row;

  const { user: authUser } = useAuthContext();

  const confirm = useBoolean();

  const popover = usePopover();

  const canManage =
    isEditable &&
    hasPermission(
      authUser,
      'any',
      AuthPermissions.UPDATE_PACKAGING_RETURN,
      AuthPermissions.DELETE_PACKAGING_RETURN,
      AuthPermissions.ADMIN_PERMISSION,
      AuthPermissions.SUPER_ADMIN_PERMISSION
    );

  return (
    <>
      <Link
        component={RouterLink}
        href={paths.dashboard.packagingReturn.view(_id)}
        sx={{
          textDecoration: 'none',
          color: 'inherit',
          display: 'contents',
        }}
      >
        <TableRow hover>
          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>{returnCode}</TableCell>

          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
            {receivedBy?.firstName}
            {receivedBy?.lastName ? ` ${receivedBy.lastName}` : ''}
          </TableCell>

          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
            {fDateTime(returnDate)}
          </TableCell>

          <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
            <Link component={RouterLink} href={paths.dashboard.packagingReturn.view(_id)} mr={2}>
              <Button variant="soft" color="primary">
                View More
              </Button>
            </Link>

            {canManage && (
              <Tooltip title="More options">
                <IconButton
                  color={popover.open ? 'inherit' : 'default'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    popover.onOpen(e);
                  }}
                >
                  <Iconify icon="eva:more-vertical-fill" />
                </IconButton>
              </Tooltip>
            )}

            {!isEditable && (
              <Tooltip title="Only the most recently created return can be edited or deleted">
                <span>
                  <IconButton disabled>
                    <Iconify icon="eva:lock-outline" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>
      </Link>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 140 }}
      >
        <MenuItem
          onClick={() => {
            onEditRow();
            popover.onClose();
          }}
        >
          <Iconify icon="solar:pen-bold" />
          Edit
        </MenuItem>

        <MenuItem
          onClick={() => {
            confirm.onTrue();
            popover.onClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon="solar:trash-bin-trash-bold" />
          Delete
        </MenuItem>
      </CustomPopover>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Delete Return"
        content={`Are you sure you want to delete ${returnCode}? This will reverse its stock effect.`}
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              confirm.onFalse();
              onDeleteRow();
            }}
          >
            Delete
          </Button>
        }
      />
    </>
  );
}
